import React from 'react';
import { Select, MenuItem, SelectChangeEvent, Checkbox } from '@mui/material';

import { colors } from 'Constants/Colors';
import { CustomSelectContainer, CustomSelectLabel } from './CustomMultipleSelect.styled';

interface Option {
  value: string | number;
  label: string;
}

interface Props {
  label?: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  options?: Option[];
  width?: string;
  disabled?: boolean;
}

const CustomMultipleSelect = ({
  label,
  value = [],
  onChange,
  placeholder,
  options = [],
  width = 'fit-content',
  disabled = false,
}: Props) => {
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    const selectedValues = typeof value === 'string' ? value.split(',') : value;

    const isSelectingTodas = selectedValues.includes('Todas');

    if (isSelectingTodas) {
      if (value.length === 1 && value[0] === 'Todas') {
        const allOptionValues = options.map(opt => opt.value.toString());
        onChange?.(allOptionValues);
      } else if (value.includes('Todas')) {
        onChange?.([]);
      } else {
        const allOptionValues = options.map(opt => opt.value.toString());
        onChange?.(allOptionValues);
      }
    } else {
      onChange?.(selectedValues.filter(val => val !== 'Todas'));
    }
  };

  const renderSelectedLabels = () => {
    if (value.length === 0) {
      return <span style={{ color: colors.GRAY_LIGHT }}>{placeholder}</span>;
    }

    const selectedLabels = options
      .filter(option => value.includes(option.value.toString()))
      .map(option => option.label);

    if (selectedLabels.length < 3) {
      return selectedLabels;
    }

    return `Terminal (${selectedLabels.length})`;
  };

  return (
    <CustomSelectContainer style={{ width }}>
      <CustomSelectLabel hidden={!label}>{label}</CustomSelectLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        displayEmpty
        variant="standard"
        disabled={disabled}
        disableUnderline
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: '300px',
              '& .MuiMenuItem-root': {
                color: 'black',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 16px',
                fontSize: '1rem',
                '&.Mui-selected': {
                  backgroundColor: `${colors.PRIMARY}20`,
                  '&:hover': {
                    backgroundColor: `${colors.PRIMARY}30`,
                  },
                },
                '&:hover': {
                  backgroundColor: `${colors.PRIMARY}10`,
                },
              },
            },
          },
        }}
        sx={{
          height: '50px',
          minWidth: '120px',
          '& .MuiSelect-select': {
            color: value && value.length > 0 ? 'black' : colors.GRAY,
            height: '50px !important',
            lineHeight: '50px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            minWidth: 'auto',
            maxWidth: '196px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '1rem',
            background: 'transparent !important',
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: '24px',
            overflow: 'hidden',
          },
          '& .MuiSelect-icon': {
            color: colors.PRIMARY,
            top: 'calc(50% - 12px)',
          },
        }}
        renderValue={renderSelectedLabels}
      >
        <MenuItem value="Todas">Todas</MenuItem>
        {options.map(option => {
          return (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={value.includes(option.value.toString())} />
              {option.label}
            </MenuItem>
          );
        })}
      </Select>
    </CustomSelectContainer>
  );
};

export default CustomMultipleSelect;
